import { FiMinus } from "@react-icons/all-files/fi/FiMinus";
import { FiPlus } from "@react-icons/all-files/fi/FiPlus";
import { memo, useMemo } from "react";
import styled from "styled-components";

import {
  useCheckElysian,
  useUpdateCubeTicket,
  useUpdateHellKey,
} from "@core/hooks/mutations/todo";
import useCubeCharacters from "@core/hooks/queries/cube/useCubeCharacters";
import { updateCharacterQueryData } from "@core/lib/queryClient";
import type { Character } from "@core/types/character";
import { getCubeTicketKeys } from "@core/utils";

type CounterType = "elysian" | "hellKey" | "cubeTicket";

interface Props {
  character: Character;
  type: CounterType;
}

/**
 * 카운터 셀 (낙원 0~5 / 지옥 열쇠 / 큐브 티켓).
 * +/- 버튼으로 조작. 설정 꺼져 있으면 "-" 표시.
 * 낙원은 5/5면 체크 스타일.
 * 큐브 티켓은 linkCubeCal 이 켜져 있으면 cubeCharacters 합계 표시 (읽기전용).
 */
const CounterCell = ({ character, type }: Props) => {
  const getCubeCharacters = useCubeCharacters();

  const show =
    type === "elysian"
      ? character.settings.showElysian
      : type === "hellKey"
        ? character.settings.showHellKey
        : character.settings.showCubeTicket;

  const cubeCharacter = useMemo(() => {
    if (type !== "cubeTicket" || !getCubeCharacters.data) return null;
    return (
      getCubeCharacters.data.find(
        (cube) => cube.characterName === character.characterName
      ) ?? null
    );
  }, [getCubeCharacters.data, character.characterName, type]);

  const linkedCube =
    type === "cubeTicket" &&
    character.settings.linkCubeCal &&
    !!cubeCharacter;

  const totalCubeTickets = useMemo(() => {
    if (linkedCube && cubeCharacter) {
      return getCubeTicketKeys(cubeCharacter).reduce(
        (acc, key) => acc + (cubeCharacter[key] as number),
        0
      );
    }
    return character.cubeTicket;
  }, [linkedCube, cubeCharacter, character.cubeTicket]);

  const value =
    type === "elysian"
      ? character.elysianCount
      : type === "hellKey"
        ? character.hellKey
        : totalCubeTickets;
  const max = type === "elysian" ? 5 : undefined;

  const checkElysian = useCheckElysian({
    onSuccess: (updated) => updateCharacterQueryData({ character: updated }),
  });
  const updateHellKey = useUpdateHellKey({
    onSuccess: (updated) => updateCharacterQueryData({ character: updated }),
  });
  const updateCubeTicket = useUpdateCubeTicket({
    onSuccess: (updated) => updateCharacterQueryData({ character: updated }),
  });

  if (!show) {
    return <Empty>-</Empty>;
  }

  const canDecrement = value > 0;
  const canIncrement = type === "elysian" ? value < 5 : true;
  const readOnly = linkedCube;
  const isDone = type === "elysian" && value >= 5;

  const handleDecrement = () => {
    if (!canDecrement) return;
    if (type === "elysian") {
      checkElysian.mutate({
        characterId: character.characterId,
        action: "DECREMENT",
      });
    } else if (type === "hellKey") {
      updateHellKey.mutate({ characterId: character.characterId, num: -1 });
    } else {
      updateCubeTicket.mutate({ characterId: character.characterId, num: -1 });
    }
  };

  const handleIncrement = () => {
    if (!canIncrement) return;
    if (type === "elysian") {
      checkElysian.mutate({
        characterId: character.characterId,
        action: "INCREMENT",
      });
    } else if (type === "hellKey") {
      updateHellKey.mutate({ characterId: character.characterId, num: 1 });
    } else {
      updateCubeTicket.mutate({ characterId: character.characterId, num: 1 });
    }
  };

  return (
    <Wrapper $isDone={isDone}>
      {!readOnly && (
        <Btn
          type="button"
          onClick={handleDecrement}
          disabled={!canDecrement}
          aria-label="감소"
        >
          <FiMinus />
        </Btn>
      )}
      <Value>
        {value}
        {max != null ? `/${max}` : ""}
      </Value>
      {!readOnly && (
        <Btn
          type="button"
          onClick={handleIncrement}
          disabled={!canIncrement}
          aria-label="증가"
        >
          <FiPlus />
        </Btn>
      )}
    </Wrapper>
  );
};

export default memo(CounterCell);

const Wrapper = styled.div<{ $isDone: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  min-height: 44px;
  padding: 2px;
  opacity: ${({ $isDone }) => ($isDone ? 0.5 : 1)};
`;

const Btn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.app.bg.white};
  color: ${({ theme }) => theme.app.text.dark2};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.app.bg.gray1};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const Value = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.app.text.dark2};
  min-width: 28px;
  text-align: center;
`;

const Empty = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 44px;
  color: ${({ theme }) => theme.app.text.light2};
  font-size: 13px;
`;
